// 오늘의 시장 이슈 (/market) — 시안 Valuation Agent v3 의 '시장 이슈' 화면.
//
// v3 에서 맞춘 것: 목록 카드(제목 16px, 88px 정사각 썸네일이 두 행에 걸침, 태그는
// wash 배경 + 가로 스크롤), 상세는 panel 배경 위 720px 흰 카드에 툴바를 품고
// 썸네일 132px 정사각, 요약 15px/1.85, 관련 질문은 테두리 없는 wash 블록.
// 목록 폭은 사용자가 끌어 조절한다 — 손잡이는 v3 처럼 보이지 않게 두고 커서만 바뀐다.
//
// 서버(market_issues.py)가 정시마다 스냅샷을 만들어 두므로 이 화면은 조회만 한다.
// 시세는 그 스냅샷에 없다 — 상세를 열 때 그 이슈의 종목만 따로 조회한다(목록에서
// 전 종목을 조회하면 이슈 12건 × 종목 수만큼 요청이 나간다).
//
// 요약·기업별 방향성은 우리가 만들지 않는다 — 서버가 V2 토픽 API 에서 그대로 가져온다
// (valuation_agent/market_issues.py). 이 화면은 표시만 한다.
//
// 관련 질문의 답은 V2 가 질문과 함께 내려준다(한 문장). 펼칠 때 /agent/market/answer
// 로 상세 답(3문단)을 받아 그 자리를 교체한다 — 키가 없으면 짧은 답이 그대로 남는다.
//
// 이 화면은 기능이 아니라 콘텐츠 소비가 목적이다(Bishop 방향) — 우측 기업 패널,
// 상단 이동/패널 버튼, "이 이슈로 분석하기" CTA 를 뺐다. 요약에서 "첫째~넷째" 나열도
// 서버가 걸러낸다(market_issues.strip_insight_list).
//
// 시안과 다른 곳:
//  · 영향 요약 칩(▲판가 상승) — 소스가 없다. 관련 키워드가 그 자리를 대신한다
//  · 기업 한 줄 소개 — 회사 개요 조회를 붙이지 않았다
//  · 뉴스 건수는 토픽 대표 기사(상한 10건) 기준이라 "관련 기사 N건" 으로만 말한다
//  · 전일 대비 배수 — 비교 기준 키워드가 너무 넓거나(삼성전자 382건 vs 889건) 너무
//    좁아(0건) 신뢰할 수 없다. 응답의 news 필드로 내려오지만 표시하지 않는다
//  · 팔로우 — 읽음 표시만 localStorage 에 둔다(계정 귀속은 다음 단계)
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { api, authHeaders } from "../lib/api";
import { useDebounced, useDocs, type MkDoc } from "../lib/marketDocs";
import { VAChart } from "../lib/vachart";
import { CompanyLogo } from "../ui/CompanyLogo";
import { KeywordRow } from "../ui/KeywordRow";
import { PdfViewer } from "../ui/PdfViewer";
import { SheetDim, SheetGrabber } from "../ui/Sheet";
import { isMobile } from "../lib/useMobileSide";
import MarketCompanyPanel, { fullSym, isListed } from "./MarketCompanyPanel";
import MarketTrend, { TREND_MAX } from "./MarketTrend";
import "../styles/lab.css";
import "../styles/market.css";

const stroke = {
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.7,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
};

export interface MkCompany {
  symbol: string;
  name: string;
  market?: string;
  exchange?: string;
  docCount?: number;
  /** V2 기사의 sentiment 를 그대로 옮긴 것 — 긍정 · 부정 · 중립. 값이 없으면 빈
   *  문자열이고 배지를 붙이지 않는다(절반 가까이가 null 로 온다). */
  rel?: string;
}
interface MkSource { pub?: string; title?: string; time?: string; url?: string }
interface MkIssue {
  uid: string;
  section: string;
  title: string;
  rank?: number;
  articleCount: number;
  keywords: string[];
  oneLine: string;
  paras: string[];
  qa: { q: string; a: string }[];
  companies: MkCompany[];
  sources: MkSource[];
  thumb: string;
  summarized: boolean;
}
interface MkSection { key: string; label: string }
interface MkSnapshot {
  timestamp: string;
  builtAt: string;
  sections: MkSection[];
  issues: MkIssue[];
  summarizedCount: number;
  articleCount: number;
  topicTotal: number;
}

// 카테고리 탭은 스냅샷이 알려준다 — 서버 SECTIONS 가 늘어도 화면을 고치지 않는다
const REL_CLASS: Record<string, string> = { 긍정: "gain", 부정: "loss", 중립: "flat" };

/** 문서 종류 탭 — 토픽은 정시 스냅샷, 나머지는 서버 검색이다.
 *  '뉴스' 탭은 두지 않는다: 뉴스 목록만 나열하면 토픽(뉴스를 묶어 요약한 결과)과
 *  겹쳐 보인다. 뉴스는 '트렌드'(시계열)로 성격을 바꿀 때만 새 정보가 된다. */
const DOC_TABS: { key: string; label: string }[] = [
  { key: "topic", label: "토픽" },
  { key: "trend", label: "트렌드" },
  { key: "research", label: "리포트" },
  { key: "ir", label: "IR" },
  { key: "disclosure", label: "공시" },
  { key: "patent", label: "특허" },
];

/** 문서 종류별 아이콘 — IR·공시·특허는 썸네일이 없어(항상 null) 자리를 이것으로 채운다.
 *  비워 두면 카드마다 폭이 달라져 목록이 들쭉날쭉해진다. */
function DocIcon({ type }: { type: string }) {
  const path = type === "research"
    ? "M4 4h11l5 5v11H4z M15 4v5h5"                       // 리포트 — 문서
    : type === "ir"
      ? "M4 19h16 M7 16V9 M12 16V5 M17 16v-6"             // IR — 실적 막대
      : type === "disclosure"
        ? "M6 3h9l4 4v14H6z M9 12h7 M9 16h5"              // 공시 — 서류
        : "M12 3l3 4.5h-6L12 3z M5 21l7-11 7 11z";        // 특허 — 도면
  return (
    <span className="mk-thumb mk-thumb-icon" aria-hidden="true">
      <svg viewBox="0 0 24 24" {...stroke}><path d={path} /></svg>
    </span>
  );
}
const READ_KEY = "va_market_read";
const WIDTH_KEY = "va_market_list_w";
/** 내가 검색한 트렌드 키워드 — 투자 테마 목록에 없는 것은 여기 담긴다 */
const MY_KW_KEY = "va_market_trend_kws";
/** 담아 두는 개수 — 넘치면 오래된 것부터 밀려난다. 왼쪽 목록은 테마 43개도 함께 있다 */
const MY_KW_MAX = 20;
const W_MIN = 300;
const W_MAX = 560;
const W_DEFAULT = 380;
const DOW = ["일", "월", "화", "수", "목", "금", "토"];

/** 문자열을 한 글자씩 흘려 보여준다 — 관련 질문의 답변에 쓴다.
 *
 *  요약 본문은 타이핑하지 않는다(Bishop 판단) — 읽으러 온 글이 흐르면 오히려 방해다.
 *  답변은 펼치는 순간 생기는 것처럼 보이는 편이 자연스럽다.
 *
 *  주의: requestAnimationFrame 은 숨은 탭에서 아예 호출되지 않는다. 안전장치 없이는
 *  탭을 옮겼다 오면 답변이 영구히 비어 있다(실측). 그래서
 *   · prefers-reduced-motion 이거나 document.hidden 이면 즉시 전문
 *   · rAF 가 멈춰도 예상 시간 뒤 setTimeout 이 전문을 채우고 visibilitychange 로도 복구
 */
const TYPE_CPS = 900;      // 초당 글자 수
const SOURCES_SHOWN = 3;   // 관련 뉴스 기본 노출 건수 — 나머지는 '더보기'

function useTyped(text: string, key: string): string {
  const [len, setLen] = useState(text.length);

  useEffect(() => {
    if (!text) return;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduce || document.hidden) {
      setLen(text.length);
      return;
    }
    setLen(0);
    let raf = 0;
    const t0 = performance.now();
    const step = (now: number) => {
      const n = Math.floor(((now - t0) / 1000) * TYPE_CPS);
      if (n >= text.length) {
        setLen(text.length);
        return;
      }
      setLen(n);
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    const guard = window.setTimeout(
      () => setLen(text.length),
      (text.length / TYPE_CPS) * 1000 + 400,
    );
    const onShow = () => {
      if (document.hidden) setLen(text.length);
    };
    document.addEventListener("visibilitychange", onShow);
    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(guard);
      document.removeEventListener("visibilitychange", onShow);
    };
    // key(질문)가 바뀌면 처음부터 다시 흐른다
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, key]);

  return text.slice(0, len);
}

/** 관련 질문의 답변 — 펼치는 순간 흐른다. */
function QaAnswer({ text, tkey }: { text: string; tkey: string }) {
  const typed = useTyped(text, tkey);
  return (
    <div className="mk-qa-a">
      {typed.split(/\n{2,}/).map((para, k) => <p key={k}>{para}</p>)}
    </div>
  );
}

/** 관련 기업 한 줄 — 상장기업이면 버튼이 되어 기업 패널(주가·기본 정보)을 연다.
 *  심볼이 없거나 비상장(NICE)이면 시세가 없으므로 눌리지 않는 행 그대로 둔다. */
function CoRow({ c, active, onPick, children }: {
  c: MkCompany;
  active: boolean;
  onPick: (c: MkCompany) => void;
  children: ReactNode;
}) {
  if (!isListed(c)) return <div className="mk-co-item">{children}</div>;
  return (
    <button
      type="button"
      className={`mk-co-item${active ? " on" : ""}`}
      title={`${c.name} 주가·기본 정보 보기`}
      onClick={() => onPick(c)}
    >
      {children}
      <svg viewBox="0 0 24 24" {...stroke}><path d="m9 18 6-6-6-6" /></svg>
    </button>
  );
}

function readSet(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(READ_KEY) || "[]"));
  } catch {
    return new Set();
  }
}

function savedWidth(key: string, min: number, max: number, fallback: number): number {
  const v = Number(localStorage.getItem(key) || 0);
  return v >= min && v <= max ? v : fallback;
}

/** 담아 둔 내 키워드 — 브라우저에만 남긴다(읽음 표시와 같은 방식) */
function savedKeywords(): string[] {
  try {
    const v: unknown = JSON.parse(localStorage.getItem(MY_KW_KEY) || "[]");
    if (!Array.isArray(v)) return [];
    return v.filter((k): k is string => typeof k === "string" && !!k.trim()).slice(0, MY_KW_MAX);
  } catch {
    return [];
  }
}

/** 서버 응답을 화면이 기대하는 모양으로 맞춘다.
 *
 *  필드가 하나 비면 앱 전체가 백지가 된다 — 에러 바운더리가 없어서 MarketPage 의
 *  예외가 루트까지 올라간다. 실제로 그랬다: 배열 필드를 추가한 뒤에도 서버가 이전
 *  모양으로 캐시된 스냅샷을 주고 있어 `analyzedDocs.toLocaleString()` 에서 터졌다.
 *  롤링 배포 중에는 구 파드가 같은 응답을 줄 수 있으므로, 필드를 늘릴 때마다
 *  화면이 깨지지 않도록 여기서 한 번에 방어한다. */
function normalize(d: Partial<MkSnapshot> | null | undefined): MkSnapshot {
  const arr = <T,>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);
  const issues = arr<Partial<MkIssue>>(d?.issues).map((i) => ({
    uid: String(i.uid || ""),
    section: String(i.section || "economy"),
    title: String(i.title || ""),
    rank: Number(i.rank || 0),
    articleCount: Number(i.articleCount || 0),
    keywords: arr<string>(i.keywords),
    oneLine: String(i.oneLine || ""),
    paras: arr<string>(i.paras),
    qa: arr<{ q: string; a: string }>(i.qa),
    companies: arr<MkCompany>(i.companies),
    sources: arr<MkSource>(i.sources),
    thumb: String(i.thumb || ""),
    summarized: Boolean(i.summarized),
  }));
  return {
    timestamp: String(d?.timestamp || ""),
    builtAt: String(d?.builtAt || ""),
    sections: arr<MkSection>(d?.sections),
    issues,
    summarizedCount: Number(d?.summarizedCount || 0),
    articleCount: Number(d?.articleCount || issues.reduce((s, i) => s + i.articleCount, 0)),
    topicTotal: Number(d?.topicTotal || 0),
  };
}

/** "2026-08-13T13:00:00.000000" → { day: "8월 13일 (목)", time: "13:00", full: "2026년 8월 13일 13:00" } */
function parseStamp(iso: string) {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(iso || "");
  if (!m) return { day: "", time: "", full: "" };
  const [, y, mo, d, h, mi] = m;
  const dow = DOW[new Date(`${y}-${mo}-${d}T00:00:00`).getDay()];
  return {
    day: `${Number(mo)}월 ${Number(d)}일 (${dow})`,
    time: `${h}:${mi}`,
    full: `${y}년 ${Number(mo)}월 ${Number(d)}일 ${h}:${mi}`,
  };
}

export default function MarketPage() {
  const [snap, setSnap] = useState<MkSnapshot | null>(null);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [cat, setCat] = useState("all");
  // 문서 종류는 URL 에 둔다 — 새로고침·공유가 되고 사이드바 메뉴는 하나로 남는다
  const [sp, setSp] = useSearchParams();
  const docType = DOC_TABS.some((t) => t.key === sp.get("doc")) ? String(sp.get("doc")) : "topic";
  const isTopic = docType === "topic";
  const [pickedDocId, setPickedDocId] = useState("");
  // 트렌드 탭 — 키워드는 투자 테마에서 고르거나 검색으로 직접 넣는다(본문은 MarketTrend).
  // 최대 5개까지 골라 한 차트에서 견준다(서버 news_trend.MAX_QUERIES 와 같은 수).
  const [trendKeywords, setTrendKeywords] = useState<string[]>(["반도체"]);
  const [themes, setThemes] = useState<string[]>([]);
  // 내가 검색한 키워드 — 검색만 하고 목록에 남지 않으면 방금 본 키워드로 되돌아갈 수
  // 없다(Bishop). 테마에 없는 것만 담고, 지울 수 있어야 한다.
  const [myKws, setMyKws] = useState<string[]>(savedKeywords);
  const [pickedUid, setPickedUid] = useState("");
  /** 모바일 상세 = 바텀 시트. 항목을 고르면 아래서 올라오고, 그래버 탭·아래로
   *  스와이프·딤 탭이면 내려간다(Bishop — 앱에서 익숙한 모달). 목록은 뒤에 남는다.
   *  데스크톱에선 CSS(display:contents)가 시트를 없던 것으로 만든다.
   *  자동 선택(첫 이슈)은 시트를 안 연다 — 들어오자마자 덮으면 목록이 있는 줄 모른다. */
  const [mDetail, setMDetail] = useState(false);
  /** 시트 2단계 — 내용을 스크롤하면 위로 붙어 전체 화면이 되고(m-full) 우상단에
   *  닫기가 생긴다. 맨 위로 되돌리면 다시 12dvh 시트로 내려온다(Bishop — 지도앱식). */
  const [mFull, setMFull] = useState(false);
  const closeSheet = () => { setMDetail(false); setMFull(false); };
  const onSheetScroll = (e: React.UIEvent) => {
    const st = (e.target as HTMLElement).scrollTop || 0;
    if (st > 24) setMFull(true);
    else if (st <= 0) setMFull(false);
  };
  /** 닫기 X — 전체 화면 상태에서 우상단 (market.css 모바일 블록) */
  const sheetClose = (
    <button type="button" className="mk-close" aria-label="닫기" onClick={closeSheet}>
      <svg viewBox="0 0 24 24" {...stroke}><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
    </button>
  );
  const [read, setRead] = useState<Set<string>>(() => readSet());
  /** 기업 패널 — 관련 기업(상장)을 고르면 우측 열/오버레이/시트로 주가·기본 정보.
   *  배치 전환은 market.css 의 .mk-aside 블록이 맡는다(.mk-root.co-open). */
  const [selCo, setSelCo] = useState<MkCompany | null>(null);
  const closeCo = useCallback(() => setSelCo(null), []);
  const [openQa, setOpenQa] = useState<number>(-1);
  const [allSources, setAllSources] = useState(false);
  // 좌우 패널 폭 — 사용자가 끌어 조절하고 브라우저에 남긴다
  const [listW, setListW] = useState<number>(() => savedWidth(WIDTH_KEY, W_MIN, W_MAX, W_DEFAULT));
  const [dragging, setDragging] = useState("");
  // 상세 답변 캐시 — V2 의 짧은 답을 먼저 보여주고, 서버가 상세 답을 주면 교체한다.
  // 키(analytics)가 없으면 서버가 ok:false 를 주고 짧은 답이 그대로 남는다.
  const [longAnswers, setLongAnswers] = useState<Record<string, string>>({});
  const asked = useRef<Set<string>>(new Set());
  // 종목별 등락 — 상세를 열 때 그 이슈의 종목만 채운다
  const [chg, setChg] = useState<Record<string, { close: number; pct: number }>>({});
  const chgRef = useRef(chg);
  chgRef.current = chg;

  // 차트 위젯의 인증 fetcher — ChatPage 와 같은 주입. 채팅을 거치지 않고 /market 에
  // 바로 들어와도 기업 패널의 주가 위젯이 첫 조회·기간 전환·비교 검색을 할 수 있다.
  useEffect(() => {
    VAChart.setFetcher(async (url: string) => {
      const r = await fetch(url, { headers: authHeaders() });
      if (!r.ok) throw new Error("HTTP " + r.status);
      return r.json();
    });
  }, []);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const d = await api<{ ok: boolean; data: MkSnapshot; message?: string }>("/agent/market/issues");
        if (!alive) return;
        if (!d.ok) throw new Error(d.message || "시장 이슈를 불러오지 못했습니다.");
        setSnap(normalize(d.data));
      } catch (e) {
        if (alive) setError(e instanceof Error ? e.message : "시장 이슈를 불러오지 못했습니다.");
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const issues = useMemo(() => snap?.issues || [], [snap]);

  const shown = useMemo(() => {
    const q = query.trim();
    return issues.filter((it) => {
      if (cat !== "all" && it.section !== cat) return false;
      if (!q) return true;
      const hay = [it.title, it.oneLine, ...(it.keywords || []),
        ...(it.companies || []).map((c) => c.name)].join(" ");
      return hay.includes(q);
    });
  }, [issues, cat, query]);

  // 첫 이슈 자동 선택 — 필터를 바꿔 현재 선택이 목록에서 사라지면 다시 첫 건으로
  useEffect(() => {
    if (!shown.length) return;
    if (!shown.some((it) => it.uid === pickedUid)) setPickedUid(shown[0].uid);
  }, [shown, pickedUid]);

  const iss = shown.find((it) => it.uid === pickedUid) || null;

  // 보던 이슈·문서·탭이 바뀌면 기업 패널을 리셋한다 — 새 맥락에 없는 기업이 남으면
  // 안 된다. 토픽 탭 + 4열이 서는 와이드 데스크톱(>1120px)에선 첫 상장기업을 기본으로
  // 바로 보여준다(Bishop). 오버레이(≤1120px)·모바일은 자동으로 열면 본문·목록을
  // 덮어버리므로 닫힌 채 시작하고, 사용자가 기업을 눌러야 연다.
  useEffect(() => {
    const wide = window.innerWidth > 1120;
    setSelCo(isTopic && wide ? (iss?.companies || []).find(isListed) || null : null);
  }, [iss, pickedDocId, docType, isTopic]);

  // 문서 탭 데이터 — 토픽·트렌드 탭에서는 훅이 조회하지 않는다(문서 종류가 아니다)
  const isTrend = docType === "trend";
  const docQuery = useDebounced(query, 350);
  const docs = useDocs(isTrend ? "" : docType, docQuery);
  const pickedDoc: MkDoc | null =
    docs.items.find((d) => d.id === pickedDocId) || docs.items[0] || null;

  // 투자 테마 43개 — 목록을 프론트에 복사해 두면 서버와 갈라진다
  useEffect(() => {
    if (!isTrend || themes.length) return;
    void api<{ ok: boolean; themes?: string[] }>("/agent/news-trend/themes")
      .then((d) => setThemes(d.themes || []))
      .catch(() => setThemes([]));
  }, [isTrend, themes.length]);

  // 왼쪽 목록에 보일 키워드 — 검색창 글자로 두 그룹을 함께 걸러낸다. 테마에 담긴 말이
  // 내 키워드에도 있으면(먼저 검색한 뒤 테마 목록이 늦게 온 경우) 테마 쪽만 남긴다.
  const kwQuery = query.trim();
  const myHits = myKws.filter(
    (k) => !themes.includes(k) && (!kwQuery || k.includes(kwQuery)),
  );
  const themeHits = themes.filter((t) => !kwQuery || t.includes(kwQuery));

  // 읽음 표시 — 계정 귀속 대신 브라우저에만 남긴다
  useEffect(() => {
    if (!iss) return;
    setOpenQa(-1);
    setAllSources(false);
    setRead((prev) => {
      if (prev.has(iss.uid)) return prev;
      const next = new Set(prev).add(iss.uid);
      try {
        localStorage.setItem(READ_KEY, JSON.stringify([...next]));
      } catch {
        /* noop */
      }
      return next;
    });
  }, [iss]);

  // 상세의 종목 등락 — 마지막 두 종가로 계산한다. 이미 받은 종목은 건너뛴다.
  const loadChg = useCallback(async (cos: MkCompany[]) => {
    const todo = cos.filter((c) => c.symbol && !(c.symbol in chgRef.current)).slice(0, 6);
    for (const c of todo) {
      // fullSym — 접두가 이미 붙어 온 심볼에 KRX: 를 또 붙이면 조회가 조용히 실패한다
      const sym = fullSym(c);
      try {
        const r = await fetch(`/agent/stock-price?symbol=${encodeURIComponent(sym)}&days=20`,
          { headers: authHeaders() });
        const d = await r.json();
        const pts: { close?: number }[] = d?.points || [];
        const last = pts[pts.length - 1]?.close;
        const prev = pts[pts.length - 2]?.close;
        if (typeof last !== "number" || typeof prev !== "number" || !prev) continue;
        setChg((m) => ({ ...m, [c.symbol]: { close: last, pct: ((last - prev) / prev) * 100 } }));
      } catch {
        /* 시세는 보조 정보다 — 실패해도 화면은 그대로 둔다 */
      }
    }
  }, []);

  useEffect(() => {
    if (iss?.companies?.length) void loadChg(iss.companies);
  }, [iss, loadChg]);

  /** 패널 폭 끌기 — 문서 전체에 리스너를 걸어 커서가 손잡이를 벗어나도 이어진다.
   *  우측 패널은 왼쪽으로 끌 때 넓어지므로 dir 로 방향을 뒤집는다. */
  const startResize = (e: React.MouseEvent) => {
    e.preventDefault();
    const cfg = { start: listW, min: W_MIN, max: W_MAX, key: WIDTH_KEY, set: setListW, dir: 1 };
    const startX = e.clientX;
    setDragging("list");
    document.body.classList.add("mk-resizing");
    const move = (ev: MouseEvent) => {
      const delta = (ev.clientX - startX) * cfg.dir;
      cfg.set(Math.min(cfg.max, Math.max(cfg.min, cfg.start + delta)));
    };
    const up = () => {
      document.removeEventListener("mousemove", move);
      document.removeEventListener("mouseup", up);
      document.body.classList.remove("mk-resizing");
      setDragging("");
      cfg.set((w) => {
        try {
          localStorage.setItem(cfg.key, String(w));
        } catch {
          /* noop */
        }
        return w;
      });
    };
    document.addEventListener("mousemove", move);
    document.addEventListener("mouseup", up);
  };

  /** 문서 종류 전환 — URL 을 바꾸고 선택·검색을 초기화한다. */
  const pickDoc = (key: string) => {
    if (key === docType) return;
    setPickedDocId("");
    setQuery("");
    setAllSources(false);
    setMDetail(false); setMFull(false); // 탭을 바꿨으면 모바일에선 목록부터

    const next = new URLSearchParams(sp);
    if (key === "topic") next.delete("doc");
    else next.set("doc", key);
    setSp(next);
  };

  /** 내가 검색한 키워드를 왼쪽 목록에 담는다 — 최근 것이 위로 온다.
   *  투자 테마에 이미 있는 말은 담지 않는다(같은 줄이 두 번 보인다). */
  const rememberKeyword = (kw: string) => {
    const k = kw.trim();
    if (!k || themes.includes(k)) return;
    setMyKws((prev) => {
      const next = [k, ...prev.filter((x) => x !== k)].slice(0, MY_KW_MAX);
      try {
        localStorage.setItem(MY_KW_KEY, JSON.stringify(next));
      } catch {
        /* noop */
      }
      return next;
    });
  };

  /** 목록에서 지우기 — 보고 있던 키워드를 지웠으면 남은 것으로 옮긴다.
   *  차트를 비워 두면 무엇을 보던 화면인지 사라진다(비교 칩 빼기와 같은 규칙). */
  const forgetKeyword = (kw: string) => {
    const rest = myKws.filter((x) => x !== kw);
    setMyKws(rest);
    try {
      localStorage.setItem(MY_KW_KEY, JSON.stringify(rest));
    } catch {
      /* noop */
    }
    setTrendKeywords((prev) => {
      if (!prev.includes(kw)) return prev;
      const next = prev.filter((x) => x !== kw);
      if (next.length) return next;
      const fallback = rest[0] || themes[0] || "";
      return fallback ? [fallback] : prev;
    });
  };

  /** 비교 키워드 더하기 — 차트 안 비교 바에서만 쓴다(옵션 기능).
   *  담은 순서를 지키고, 상한(TREND_MAX)에 닿으면 더 담지 않는다.
   *  직접 넣은 말은 왼쪽 목록에도 담는다 — 칩을 빼면 다시 찾을 길이 없다. */
  const addKeyword = (kw: string) => {
    const k = kw.trim();
    if (!k) return;
    rememberKeyword(k);
    setTrendKeywords((prev) =>
      prev.includes(k) || prev.length >= TREND_MAX ? prev : [...prev, k]);
  };

  /** 빼기 — 마지막 하나는 남긴다(빈 화면이 되면 무엇을 보던 화면인지 사라진다).
   *  차트 비교 칩에서 빼는 것이라 왼쪽 목록에서는 지우지 않는다. */
  const removeKeyword = (kw: string) =>
    setTrendKeywords((prev) => (prev.length > 1 ? prev.filter((x) => x !== kw) : prev));

  const resetWidth = () => {
    setListW(W_DEFAULT);
    try {
      localStorage.setItem(WIDTH_KEY, String(W_DEFAULT));
    } catch {
      /* noop */
    }
  };


  /** 질문을 펼칠 때 상세 답변을 받아 짧은 답을 교체한다. 한 번 시도한 질문은 다시
   *  부르지 않는다. 키가 없거나 실패하면 짧은 답(q.a)이 그대로 남는다. */
  const loadLongAnswer = useCallback(async (issue: MkIssue, q: { q: string; a: string }) => {
    const key = `${issue.uid}|${q.q}`;
    if (asked.current.has(key)) return;
    asked.current.add(key);
    try {
      const d = await api<{ ok: boolean; data?: { answer: string } }>(
        `/agent/market/answer?uid=${encodeURIComponent(issue.uid)}`
        + `&section=${encodeURIComponent(issue.section)}`
        + `&question=${encodeURIComponent(q.q)}`,
      );
      if (d.ok && d.data?.answer) setLongAnswers((m) => ({ ...m, [key]: d.data!.answer }));
    } catch {
      /* 짧은 답을 유지한다 */
    }
  }, []);

  const pct = (p: number) => `${p > 0 ? "+" : ""}${p.toFixed(1)}%`;
  const dirOf = (p: number) => (p > 0 ? "up" : p < 0 ? "down" : "");
  const sectionLabel = (key: string) =>
    snap?.sections.find((x) => x.key === key)?.label || key;

  /** 문서 종류 탭 — 모든 탭이 같은 것을 쓴다(트렌드는 전체폭 머리글에서 쓴다) */
  const docTabs = (
    <div className="mk-docs">
      {DOC_TABS.map((t) => (
        <button
          key={t.key}
          type="button"
          className={`mk-doc${docType === t.key ? " on" : ""}`}
          onClick={() => pickDoc(t.key)}
        >
          {t.label}
        </button>
      ))}
    </div>
  );

  // 토픽 탭만 스냅샷을 기다린다 — 문서 탭은 스냅샷과 무관하게 뜬다
  if (isTopic && error) {
    return <div className="mk-state">{error}<br />잠시 후 다시 시도해 주세요.</div>;
  }
  if (isTopic && !snap) {
    return <div className="mk-state">시장 이슈를 정리하고 있습니다…</div>;
  }

  const ts = parseStamp(snap?.timestamp || "");

  return (
    /* display:contents — .mk-main(flex row)의 직계 자식 배치를 그대로 두면서
       모바일 목록/상세 전환 클래스(m-detail)를 얹을 자리만 만든다 (.dcf-root 와 같은 수법) */
    <div
      className={`mk-root${mDetail ? " m-detail" : ""}${mFull ? " m-full" : ""}${selCo && !isTrend ? " co-open" : ""}`}
      style={{ display: "contents" }}
    >
      {/* 모바일 시트 뒤 딤 — 탭하면 닫힌다. 데스크톱에선 CSS 가 숨긴다 */}
      <SheetDim className="mk-dim" onClose={closeSheet} />

      {/* ── 좌: 이슈 목록 ───────────────────────────────── */}
      <section className="mk-list" style={{ width: listW }}>
        <div className="mk-list-head">
          <strong>시장 이슈</strong>

          {/* 문서 종류 — 1차 탭. URL(?doc=)에 남아 새로고침·공유가 된다 */}
          {docTabs}

          <div className="mk-meta">
            {isTopic && snap ? (
              <>
                {ts.day} {ts.time} 기준 · 토픽
                {snap.topicTotal ? ` ${snap.topicTotal.toLocaleString("ko-KR")}개 중 ` : " "}
                {issues.length}개 · 근거 기사 {snap.articleCount.toLocaleString("ko-KR")}건
              </>
            ) : isTrend ? (
              <>키워드가 뉴스에서 얼마나 다뤄졌는지 · 1990년~</>
            ) : (
              <>
                {/* 총건수는 보이지 않는다 — '300만 건 중 20건' 은 화면에 쓸모가
                    없고 '여기 다 있다'는 신호만 준다 */}
                {docQuery ? `'${docQuery}' 검색 ${docs.items.length}건` : `최신순 ${docs.items.length}건`}
              </>
            )}
          </div>

          {/* 래퍼는 모바일 sticky 자리 — 데스크톱에선 투명한 블록 */}
          <div className="mk-search-wrap">
          <label className="mk-search">
            <svg viewBox="0 0 24 24" {...stroke}>
              <circle cx="11" cy="11" r="7" />
              <path d="m20 20-3.5-3.5" />
            </svg>
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={
                isTopic ? "이슈·기업 검색"
                  : isTrend ? "키워드를 넣고 Enter (예: HBM)"
                    // 무엇을 뒤지는 검색인지 칸이 스스로 말한다 (예: "IR 제목·본문 검색")
                    : `${DOC_TABS.find((t) => t.key === docType)?.label || "문서"} 제목·본문 검색`
              }
              onKeyDown={(e) => {
                // 한글 IME 조합 중 Enter 는 무시 — 조합 확정 keydown 이 한 번 더
                // 발화해 마지막 글자가 새 키워드로 등록된다("나이키" → "키", Bishop 제보)
                if (e.nativeEvent.isComposing || e.keyCode === 229) return;
                // 트렌드는 입력을 확정해야 조회한다 — 글자마다 10년치를 훑지 않게
                if (isTrend && e.key === "Enter" && query.trim()) {
                  // 검색창 입력이 우선이다 — 담긴 것을 밀어내고 이 키워드로 새로 본다
                  setTrendKeywords([query.trim()]);
                  setMDetail(true); // 모바일: 조회했으면 차트를 보여준다
                  // 왼쪽 목록에도 담는다 — 검색만 하고 사라지면 방금 본 키워드로
                  // 되돌아갈 수 없다(Bishop). 테마에 있는 말이면 그 줄이 켜진다.
                  rememberKeyword(query.trim());
                  // 검색창을 비운다 — 그대로 두면 테마 목록이 그 글자로 걸러진 채 남아
                  // 다른 테마로 옮겨갈 수 없다(QA 에서 1개만 남는 것을 확인)
                  setQuery("");
                }
              }}
            />
          </label>
          </div>

          {/* 섹션 탭은 토픽에만 있다 — 문서는 섹션 구분이 없다 */}
          {isTopic && snap && (
            <div className="mk-cats">
              {[{ key: "all", label: "전체" }, ...snap.sections].map((c) => (
                <button
                  key={c.key}
                  type="button"
                  className={`mk-cat${cat === c.key ? " on" : ""}`}
                  onClick={() => setCat(c.key)}
                >
                  {c.label}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="mk-items">
          {/* ── 트렌드 탭 목록: 내 키워드 + 투자 테마 ──
              고르면 **그 키워드 하나로** 새로 본다. 비교는 차트 안에서 더한다(옵션 기능)
              — 왼쪽에서 여러 개를 누적하게 두니 무엇을 보고 있는지 헷갈렸다(Bishop).
              내가 검색한 키워드는 위에 담기고 지울 수 있다. 테마는 서버가 주는 것이라
              지우지 못한다 — 목록에서 빼면 다시 찾을 길이 없다. */}
          {isTrend && (
            <>
              {!!myHits.length && (
                <>
                  <div className="mk-kw-group">내 키워드</div>
                  {myHits.map((k) => (
                    <KeywordRow
                      key={k}
                      keyword={k}
                      selected={trendKeywords[0] === k}
                      onPick={(kw) => { setTrendKeywords([kw]); setMDetail(true); }}
                      onRemove={forgetKeyword}
                    />
                  ))}
                  <div className="mk-kw-group">투자 테마</div>
                </>
              )}
              {themeHits.map((t) => (
                <KeywordRow
                  key={t}
                  keyword={t}
                  selected={trendKeywords[0] === t}
                  onPick={(kw) => { setTrendKeywords([kw]); setMDetail(true); }}
                />
              ))}
              {!themes.length && <div className="mk-empty">테마를 불러오는 중…</div>}
              {!!themes.length && !!query.trim() && !myHits.length && !themeHits.length && (
                <div className="mk-empty">
                  '{query.trim()}' 은 목록에 없습니다.
                  <br />
                  Enter 를 누르면 이 키워드로 조회합니다.
                </div>
              )}
            </>
          )}

          {/* ── 문서 탭 목록 ── */}
          {!isTopic && !isTrend && (
            <>
              {docs.error && <div className="mk-empty">{docs.error}</div>}
              {!docs.error && !docs.items.length && !docs.loading && (
                <div className="mk-empty">
                  조건에 맞는 문서가 없습니다.
                  <br />
                  검색어를 바꿔 보세요.
                </div>
              )}
              {docs.items.map((d) => (
                <button
                  key={d.id}
                  type="button"
                  className={`mk-item${d.id === (pickedDoc?.id || "") ? " on" : ""}`}
                  onClick={() => {
                    setPickedDocId(d.id);
                    setMDetail(true);
                    // 문서는 iframe 내부 스크롤이라 스크롤로는 전체 화면이 안 된다 —
                    // 모바일에선 처음부터 전체 화면 모달(닫기 X)로 연다.
                    // 특허는 뷰어가 아니라 요약 카드라 2단 시트 그대로.
                    if (d.url && docType !== "patent") setMFull(true);
                  }}
                >
                  <div className="mk-item-main">
                    <div className="mk-item-title">{d.title || "(제목 없음)"}</div>
                    <div className="mk-item-sub">
                      {[d.pub, d.date, d.time].filter(Boolean).join(" · ")}
                    </div>
                    {!!d.companies.length && (
                      <div className="mk-item-chips">
                        {d.companies.slice(0, 3).map((c) => (
                          <span key={c.symbol || c.name} className="mk-chip">
                            <CompanyLogo symbol={c.symbol} name={c.name} size="md" />
                            {c.name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  {/* 공시는 썸네일이 없다(DART HTML 이라 표지가 없음) — 빈 아이콘
                      칸을 두면 제목이 그만큼 좁아진다. 자리를 아예 비운다. */}
                  {d.thumb
                    ? <img className="mk-thumb" src={d.thumb} alt="" loading="lazy" />
                    : docType === "disclosure" ? null : <DocIcon type={docType} />}
                </button>
              ))}
              {docs.loading && <div className="mk-empty">불러오는 중…</div>}
              {docs.hasMore && !docs.loading && (
                <button type="button" className="mk-more" onClick={docs.loadMore}>
                  더 보기
                </button>
              )}
            </>
          )}

          {/* ── 토픽 탭 목록 ── */}
          {isTopic && !shown.length && (
            <div className="mk-empty">
              조건에 맞는 이슈가 없습니다.
              <br />
              검색어나 카테고리를 바꿔 보세요.
            </div>
          )}
          {isTopic && shown.map((it) => (
            <button
              key={it.uid}
              type="button"
              className={`mk-item${it.uid === pickedUid ? " on" : ""}${read.has(it.uid) ? " read" : ""}`}
              onClick={() => { setPickedUid(it.uid); setMDetail(true); }}
            >
              <div className="mk-item-main">
                <div className="mk-item-title">{it.title}</div>
                <div className="mk-item-sub">
                  {sectionLabel(it.section)} · 관련 기사 {it.articleCount}건
                </div>
                {!!it.companies.length && (
                  <div className="mk-item-chips">
                    {it.companies.slice(0, 3).map((c) => (
                      <span key={c.symbol} className="mk-chip">
                        <CompanyLogo symbol={c.symbol} name={c.name} size="md" />
                        {c.name}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              {it.thumb && <img className="mk-thumb" src={it.thumb} alt="" loading="lazy" />}
            </button>
          ))}
        </div>
      </section>

      {/* 목록 폭 조절 — 조절 가능하다는 걸 알리려고 그립을 보여준다 */}
      <button
        type="button"
        className={`mk-grip${dragging === "list" ? " dragging" : ""}`}
        title="너비 조절 (두 번 클릭하면 기본값)"
        aria-label="이슈 목록 너비 조절"
        onMouseDown={startResize}
        onDoubleClick={resetWidth}
      />

      {/* 트렌드는 읽을 문서가 아니라 보는 도구다 — 흰 카드 대신 차트 | 기사 두 칸을 편다 */}
      {/* .mk-sheet: 데스크톱 display:contents(없는 셈), 모바일 바텀 시트.
          .mk-grab 은 시트 상단 그래버 — 탭·아래로 스와이프로 닫는다 */}
      {isTrend && (
        <div className="mk-sheet" onScrollCapture={onSheetScroll}>
          <SheetGrabber className="mk-grab" sheetSelector=".mk-sheet" onClose={closeSheet} />
          {sheetClose}
          <MarketTrend
            keywords={trendKeywords}
            themes={themes}
            onAddKeyword={addKeyword}
            onRemoveKeyword={removeKeyword}
          />
        </div>
      )}

      {/* ── 중앙: 상세 ──────────────────────────────────── */}
      {!isTrend && (
      <div className="mk-sheet" onScrollCapture={onSheetScroll}>
      <SheetGrabber className="mk-grab" sheetSelector=".mk-sheet" onClose={closeSheet} />
      {sheetClose}
      <section className="mk-detail">

        <div className="mk-scroll">
          {/* ── 문서 탭 상세 — 요약 카드가 아니라 **원문 그대로**(Bishop).
              리포트·IR 은 우리 CDN PDF 라 /agent/doc-proxy 로 content-type 을
              고쳐 iframe 에 띄우고, 공시(DART)·특허(DOI)는 원문 페이지를 그대로
              품는다(DART 는 frame 차단 없음 — 실측). 막히는 문서의 출구는
              헤더의 '새 탭'. url 이 없는 문서만 기존 요약 카드로 남는다. ── */}
          {!isTopic && (
            !pickedDoc ? (
              <div className="mk-state">
                {docs.loading ? "불러오는 중…" : "왼쪽에서 문서를 선택해 주세요."}
              </div>
            ) : pickedDoc.url && docType !== "patent" ? (
              /* 특허는 뷰어에서 뺀다 — 원문이 http(doi.org→doi.or.kr)라 https 페이지의
                 iframe 에선 mixed content 로 차단된다(실측 빈 화면). 요약 카드 + 새 탭. */
              <div className="mk-viewer">
                <div className="mk-viewer-head">
                  <div className="mk-viewer-meta">
                    <b>{pickedDoc.title || "(제목 없음)"}</b>
                    <span>
                      {[pickedDoc.pub, [pickedDoc.date, pickedDoc.time].filter(Boolean).join(" ")]
                        .filter(Boolean).join(" · ")}
                    </span>
                  </div>
                  <a className="mk-viewer-open" href={pickedDoc.url} target="_blank" rel="noreferrer">
                    <svg viewBox="0 0 24 24" {...stroke}>
                      <path d="M15 3h6v6" /><path d="M10 14 21 3" />
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                    </svg>
                    새 탭
                  </a>
                </div>
                {(() => {
                  const proxied = /^https:\/\/ddi-cdn\.deepsearch\.com\//.test(pickedDoc.url)
                    ? `/agent/doc-proxy?url=${encodeURIComponent(pickedDoc.url)}`
                    : pickedDoc.url;
                  // 모바일 PDF 는 iframe 이 못 그린다 — 안드로이드 크롬은 빈 화면,
                  // iOS 는 첫 페이지만(실사용 제보). pdfjs 캔버스 뷰어로 대신한다.
                  // 데스크톱은 네이티브 뷰어(툴바·검색)가 더 좋아 iframe 유지.
                  const isPdf = /\.pdf(\?|$)/i.test(pickedDoc.url);
                  return isPdf && isMobile() ? (
                    <PdfViewer className="mk-viewer-frame" src={proxied} />
                  ) : (
                    <iframe
                      className="mk-viewer-frame"
                      title={pickedDoc.title || "문서 원문"}
                      src={proxied}
                    />
                  );
                })()}
              </div>
            ) : (
              <div className="mk-card-wrap">
                <div className="mk-card">
                  <div className="mk-bar">
                    <div className="mk-kicker">
                      {[DOC_TABS.find((t) => t.key === docType)?.label, pickedDoc.pub,
                        [pickedDoc.date, pickedDoc.time].filter(Boolean).join(" ")]
                        .filter(Boolean).join(" · ")}
                    </div>
                  </div>

                  <div className="mk-head">
                    <div className="mk-head-main">
                      <h1>{pickedDoc.title || "(제목 없음)"}</h1>
                      {pickedDoc.author && pickedDoc.author !== pickedDoc.pub && (
                        <p className="mk-oneline" style={{ fontSize: 14, marginTop: 10 }}>
                          {pickedDoc.author}
                        </p>
                      )}
                    </div>
                    {pickedDoc.thumb && <img className="mk-head-thumb" src={pickedDoc.thumb} alt="" />}
                  </div>

                  {/* 리포트만 AI 요약이 온다(research_insight) — 토픽 요약과 같은 방식으로 그린다 */}
                  {pickedDoc.insight?.summary && (
                    <div className="mk-sec">
                      <div className="mk-sec-h">요약</div>
                      <div className="mk-paras"><p>{pickedDoc.insight.summary}</p></div>
                    </div>
                  )}

                  {!!pickedDoc.insight?.keyPoints.length && (
                    <div className="mk-sec">
                      <div className="mk-sec-h">핵심 포인트</div>
                      <ul className="mk-points">
                        {pickedDoc.insight.keyPoints.map((k, i) => <li key={i}>{k}</li>)}
                      </ul>
                    </div>
                  )}

                  {pickedDoc.summary && (
                    <div className="mk-sec">
                      <div className="mk-sec-h">{pickedDoc.insight?.summary ? "원문 발췌" : "본문"}</div>
                      <div className="mk-paras">
                        {pickedDoc.summary.split(/\n{2,}/).map((para, i) => <p key={i}>{para}</p>)}
                      </div>
                    </div>
                  )}

                  {!!pickedDoc.companies.length && (
                    <div className="mk-sec">
                      <div className="mk-sec-h">관련 기업</div>
                      <div className="mk-co">
                        {pickedDoc.companies.map((c) => (
                          <CoRow
                            key={c.symbol || c.name}
                            c={c}
                            active={!!selCo && fullSym(selCo) === fullSym(c)}
                            onPick={setSelCo}
                          >
                            <CompanyLogo symbol={c.symbol} name={c.name} size="lg" />
                            <div className="mk-co-main">
                              <div className="mk-co-name">
                                {c.name}
                                {c.rel && <span className={`mk-rel ${REL_CLASS[c.rel] || "flat"}`}>{c.rel}</span>}
                              </div>
                              {c.symbol && (
                                <div className="mk-co-why">{fullSym(c)}</div>
                              )}
                            </div>
                          </CoRow>
                        ))}
                      </div>
                    </div>
                  )}

                  {pickedDoc.url && (
                    <div className="mk-sec">
                      <div className="mk-sec-h">원문</div>
                      <div className="mk-src">
                        <a href={pickedDoc.url} target="_blank" rel="noreferrer">
                          <span>원문 보기</span>
                          <span className="pub">{pickedDoc.pub}</span>
                        </a>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )
          )}

          {/* ── 토픽 탭 상세 ── */}
          {isTopic && (!iss ? (
            <div className="mk-state">왼쪽에서 이슈를 선택해 주세요.</div>
          ) : (
            <div className="mk-card-wrap">
            <div className="mk-card">
              {/* 예전 기능 바가 있던 자리 — 경계선으로 본문과 나눈다 */}
              <div className="mk-bar">
                <div className="mk-kicker">
                  {sectionLabel(iss.section)} · {ts.full} 기준 · 관련 기사 {iss.articleCount}건
                </div>
              </div>

              <div className="mk-head">
                <div className="mk-head-main">
                  <h1>{iss.title}</h1>
                  {iss.oneLine ? (
                    <p className="mk-oneline">{iss.oneLine}</p>
                  ) : (
                    <p className="mk-oneline" style={{ color: "var(--muted)" }}>
                      요약이 아직 생성되지 않았습니다. 아래 관련 뉴스와 기업으로 확인해 주세요.
                    </p>
                  )}
                </div>
                {iss.thumb && <img className="mk-head-thumb" src={iss.thumb} alt="" />}
              </div>

              {!!iss.paras.length && (
                <div className="mk-sec">
                  <div className="mk-sec-h">요약</div>
                  <div className="mk-paras">
                    {iss.paras.map((p, i) => <p key={i}>{p}</p>)}
                  </div>
                </div>
              )}

              {!!iss.companies.length && (
                <div className="mk-sec">
                  <div className="mk-sec-h">관련 기업</div>
                  <div className="mk-co">
                    {iss.companies.map((c) => {
                      const q = chg[c.symbol];
                      return (
                        <CoRow
                          key={c.symbol || c.name}
                          c={c}
                          active={!!selCo && fullSym(selCo) === fullSym(c)}
                          onPick={setSelCo}
                        >
                          <CompanyLogo symbol={c.symbol} name={c.name} size="lg" />
                          <div className="mk-co-main">
                            <div className="mk-co-name">
                              {c.name}
                              {c.rel && <span className={`mk-rel ${REL_CLASS[c.rel] || "flat"}`}>{c.rel}</span>}
                            </div>
                            <div className="mk-co-why">이 이슈 기사에서 {c.docCount ?? 0}건 언급</div>
                          </div>
                          <div className={`mk-co-chg ${q ? dirOf(q.pct) : ""}`}>{q ? pct(q.pct) : "—"}</div>
                        </CoRow>
                      );
                    })}
                  </div>
                </div>
              )}

              {!!iss.qa.length && (
                <div className="mk-sec">
                  <div className="mk-sec-h">관련 질문</div>
                  <div className="mk-qa">
                    {iss.qa.map((q, i) => (
                      <div key={i} className={`mk-qa-item${openQa === i ? " open" : ""}`}>
                        <button
                          type="button"
                          className="mk-qa-q"
                          onClick={() => {
                            const next = openQa === i ? -1 : i;
                            setOpenQa(next);
                            if (next === i) void loadLongAnswer(iss, q);
                          }}
                        >
                          <span>{q.q}</span>
                          <svg viewBox="0 0 24 24" {...stroke}><path d="m6 9 6 6 6-6" /></svg>
                        </button>
                        {openQa === i && (
                          <QaAnswer
                            text={longAnswers[`${iss.uid}|${q.q}`] || q.a}
                            tkey={`${iss.uid}|${i}`}
                          />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 근거 기사가 0건인 이슈가 있다 — 토픽 키워드가 신조어(붙여쓰기)면
                  오늘 날짜 안에서 검색이 잡히지 않는다. 섹션을 조용히 빼면 화면이
                  고장난 것처럼 보이므로 왜 비었는지 밝힌다. */}
              <div className="mk-sec">
                <div className="mk-sec-h">관련 뉴스</div>
                {iss.sources.length ? (
                  <div className="mk-src">
                    {(allSources ? iss.sources : iss.sources.slice(0, SOURCES_SHOWN)).map((s, i) => (
                      <a key={i} href={s.url || "#"} target="_blank" rel="noreferrer">
                        <span>{s.title}</span>
                        <span className="pub">{s.pub} {s.time}</span>
                      </a>
                    ))}
                    {!allSources && iss.sources.length > SOURCES_SHOWN && (
                      <button type="button" className="mk-more" onClick={() => setAllSources(true)}>
                        더보기 {iss.sources.length - SOURCES_SHOWN}건
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="mk-none">
                    이 이슈의 근거 기사를 찾지 못했습니다. 관련 키워드로 직접 검색해 보세요.
                  </div>
                )}
              </div>


            </div>
            </div>
          ))}
        </div>
      </section>
      </div>
      )}

      {/* ── 우: 기업 정보 패널 — 관련 기업을 고르면 열린다 ─────────────
          데스크톱은 4번째 열/오버레이, 모바일은 이슈 시트 위 바텀 시트(전용 딤). */}
      {!isTrend && (
        <>
          <SheetDim className="mk-co-dim" onClose={closeCo} />
          <MarketCompanyPanel
            co={selCo}
            chg={selCo ? chg[selCo.symbol] : undefined}
            onClose={closeCo}
          />
        </>
      )}

    </div>
  );
}
